import { Component, computed, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { BehaviorSubject, merge, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, switchMap } from 'rxjs/operators';

import { AsyncPipe, NgClass } from '@angular/common';
import { AppleDeviceProfileResolverActions } from '@era/stores';
import { Store } from '@ngrx/store';
import { isOsVersionAtLeast } from '@rocksoft/shared';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';
import { AppleDevice } from '../../../../viewmodels/apple/apple-device.model';
import { QueryList } from '../../../../viewmodels/apple/commands/device-information.command';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

function commandProgressEqual(a: CommandProgress, b: CommandProgress): boolean {
  return a.commandUUID === b.commandUUID && a.status === b.status;
}

@Component({
  selector: 'ml-mdm-disable-lost-mode',
  templateUrl: './disable-lost-mode.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommandSpinnerComponent, NgClass, AsyncPipe],
})
export class DisableLostModeComponent {
  readonly apple = input.required<AppleDevice>();
  readonly class = input<string>();

  private readonly appleMdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly store = inject(Store);

  private _isProcessing = new BehaviorSubject(false);
  isEnabled: Observable<boolean | null>;

  queryList: QueryList = new QueryList();

  readonly isLostModeEnabled = computed(() => this.apple().isMDMLostModeEnabled ?? false);
  readonly isSupervised = computed(() => this.apple()?.isSupervised ?? false);
  readonly hasSupportedOS = computed(() => isOsVersionAtLeast(this.apple().osVersion, '9.3.0'));
  readonly udid = computed(() => this.apple().udid);

  private readonly _udidSignal = computed(() => this.apple().udid);

  private _commandProgressInformation: Subject<CommandProgress> = new Subject<CommandProgress>();
  readonly commandProgressInformation$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) =>
      merge(
        this._commandProgressInformation,
        this.commandProgressService.commandsOfRequestType(_udid, 'DeviceInformation')
      )
    )
  );

  private _commandProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  readonly commandProgress$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'DisableLostMode'))
    )
  );

  private commandProgressSubscription: Subscription | undefined;
  private commandProgressInfoSubscription: Subscription | undefined;

  constructor() {
    this.isEnabled = this.isProcessing.pipe(
      map((isProcessing) => {
        return (
          this.isSupervised() &&
          this.isLostModeEnabled() &&
          !isProcessing &&
          (this.hasSupportedOS() == null || this.hasSupportedOS())
        );
      })
    );

    effect(() => {
      void this.apple();
      this._isProcessing.next(false);
    });

    this.commandProgressSubscription = this.commandProgress$
      .pipe(
        filter((progress) => progress.status === 'Acknowledged'),
        distinctUntilChanged(commandProgressEqual)
      )
      .subscribe(() => {
        this.queryDeviceInformation();
      });

    // TODO: Move to central service
    this.commandProgressInfoSubscription = this.commandProgressInformation$
      .pipe(
        filter((progress) => progress.status === 'Acknowledged'),
        distinctUntilChanged(commandProgressEqual)
      )
      .subscribe((progress) => {
        this.store.dispatch(
          AppleDeviceProfileResolverActions.profileRequested({
            udid: progress.udid,
            profileType: 'device-information',
          })
        );
      });
  }

  get isProcessing(): Observable<boolean> {
    return this._isProcessing;
  }

  disableLostMode() {
    const udid = this.udid();
    if (!this.isEnabled) {
      return;
    }

    this._isProcessing.next(true);
    const command: CommandProgress = {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'DisableLostMode',
    };
    this._commandProgress.next(command);

    this.appleMdmService
      .disableLostMode(udid)
      .pipe(
        finalize(() => {
          this._isProcessing.next(false);
        })
      )
      .subscribe();
  }

  private queryDeviceInformation() {
    const udid = this.udid();
    const command: CommandProgress = {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'DeviceInformation',
    };
    this._commandProgressInformation.next(command);
    this.appleMdmService.getDeviceInformation(udid).subscribe();
  }
}
